// Stable gradient palette — picked by slug hash so each article keeps the
// same visual identity across builds. Listed as literal strings so Tailwind's
// content scanner detects them and emits the CSS.
const GRADIENT_CLASSES = [
  "from-amber-500 via-orange-500 to-red-600",
  "from-emerald-500 via-teal-500 to-cyan-600",
  "from-violet-500 via-purple-500 to-fuchsia-600",
  "from-rose-500 via-pink-500 to-fuchsia-600",
  "from-blue-500 via-indigo-500 to-purple-600",
  "from-orange-500 via-amber-500 to-yellow-600",
  "from-sky-500 via-blue-500 to-indigo-600",
  "from-lime-500 via-emerald-500 to-teal-600",
];

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getInitials = (title: string): string => {
  const words = title.split(/\s+/).filter(Boolean);
  return (
    words
      .slice(0, 2)
      .map((word) => word[0] ?? "")
      .join("")
      .toUpperCase() || "?"
  );
};

interface ArticlePlaceholderProps {
  slug: string;
  title: string;
  className?: string;
}

/**
 * Fallback visual for articles that have no `article_thumbnail_url`. Renders
 * a deterministic gradient (same article = same gradient) with the title's
 * initials in the center. Caller controls outer aspect-ratio via the parent
 * container; this component fills available space.
 */
const ArticlePlaceholder = ({ slug, title, className = "" }: ArticlePlaceholderProps) => {
  const gradient = GRADIENT_CLASSES[hashString(slug) % GRADIENT_CLASSES.length];
  const initials = getInitials(title);

  return (
    <div
      role="img"
      aria-label={title}
      className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${gradient} ${className}`}
    >
      <span className="heading-display select-none text-5xl font-bold text-white drop-shadow-md md:text-6xl">
        {initials}
      </span>
    </div>
  );
};

export default ArticlePlaceholder;
